import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crown, Check, Lightning } from "@phosphor-icons/react";

const plans = [
  {
    lookup_key: "premium_monthly",
    name: "Monthly",
    price: "$9.99",
    period: "/ month",
    tag: "Flexible",
    highlight: false,
  },
  {
    lookup_key: "premium_yearly",
    name: "Yearly",
    price: "$95.90",
    period: "/ year",
    tag: "Save 20%",
    highlight: true,
  },
];

const perks = [
  "Unlimited downloads of every pack",
  "All icon packs, widgets & wallpapers",
  "Early access to new drops",
  "Exclusive premium-only themes",
  "Support two comedians' bad decisions",
];

export default function Premium() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const subscribe = async (lookup_key) => {
    if (!user) {
      toast.info("Sign in first", { description: "Create your account to go premium." });
      return login();
    }
    if (user.is_premium) {
      toast.success("You're already premium!");
      return navigate("/marketplace");
    }
    setLoading(lookup_key);
    try {
      const res = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Could not start checkout");
      setLoading(null);
    }
  };

  return (
    <div className="grid-bg min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full mb-8">
            <Crown weight="fill" size={16} className="text-[#FFEB3B]" />
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-white/70">Premium Tier</span>
          </div>
          <h1 className="font-display font-black text-4xl lg:text-6xl tracking-tighter uppercase leading-none">
            Download <span className="text-[#FFEB3B] text-glow-yellow">everything</span>
          </h1>
          <p className="text-white/60 text-lg mt-6">
            One subscription. Unlimited downloads across the entire catalog. No per-pack pricing, no punchlines.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mt-16">
          {plans.map((p) => (
            <div
              key={p.lookup_key}
              data-testid={`plan-${p.lookup_key}`}
              className={`relative rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 ${
                p.highlight
                  ? "bg-[#0A0A14] border-2 border-[#FFEB3B] shadow-[0_0_40px_rgba(255,235,59,0.2)]"
                  : "glass border border-white/15"
              }`}
            >
              {p.highlight && (
                <span className="absolute -top-3 left-8 bg-[#FFEB3B] text-[#05050A] text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-sm">
                  Best Value
                </span>
              )}
              <div className="flex items-center justify-between">
                <h3 className="font-display font-black text-2xl uppercase tracking-tighter">{p.name}</h3>
                <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-sm ${p.highlight ? "bg-[#FFEB3B]/15 text-[#FFEB3B]" : "bg-white/10 text-white/60"}`}>
                  {p.tag}
                </span>
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display font-black text-5xl tracking-tighter">{p.price}</span>
                <span className="text-white/50">{p.period}</span>
              </div>
              <button
                data-testid={`subscribe-${p.lookup_key}`}
                onClick={() => subscribe(p.lookup_key)}
                disabled={loading === p.lookup_key}
                className={`w-full mt-8 flex items-center justify-center gap-2 font-bold py-4 rounded-sm transition-all disabled:opacity-60 ${
                  p.highlight
                    ? "bg-[#FFEB3B] text-[#05050A] hover:bg-[#fff35c] hover:shadow-[0_0_28px_rgba(255,235,59,0.5)]"
                    : "glass border border-white/20 hover:border-[#00E5FF] hover:text-[#00E5FF]"
                }`}
              >
                <Lightning weight="fill" size={18} />
                {loading === p.lookup_key ? "Redirecting…" : user?.is_premium ? "You're Premium" : "Subscribe"}
              </button>
            </div>
          ))}
        </div>

        <div className="max-w-2xl mx-auto mt-16 glass rounded-lg p-8">
          <h3 className="font-display font-bold text-lg mb-6 uppercase tracking-tight">Everything included</h3>
          <ul className="space-y-4">
            {perks.map((perk) => (
              <li key={perk} className="flex items-center gap-3 text-white/80">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-[#00E5FF]/15 shrink-0">
                  <Check weight="bold" size={14} className="text-[#00E5FF]" />
                </span>
                {perk}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-center text-white/30 text-xs mt-8">
          Test mode — use card 4242 4242 4242 4242, any future expiry, any CVC.
        </p>
      </div>
    </div>
  );
}
